import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TrendingDown, TrendingUp, Circle, Calendar, DollarSign, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface TimelineEvent {
  date: string;
  timestamp: number;
  eventType: 'ISSUANCE' | 'TRANSFER' | 'SUBSCRIPTION' | 'FUNDING_ROUND';
  description: string;
  myShares: number;
  totalShares: number;
  ownershipPct: number;
  changePct: number;
  sharePrice?: number;
  amount?: number;
}

interface OwnershipHistoryProps {
  companyId: string;
  shareholderId: string;
}

export function OwnershipHistoryTimeline({ companyId, shareholderId }: OwnershipHistoryProps) {
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId && shareholderId) {
      fetchOwnershipHistory();
    }
  }, [companyId, shareholderId]);

  const fetchOwnershipHistory = async () => {
    try {
      setLoading(true);

      // Fetch all ledger events for this company
      const { data: events, error: eventsError } = await supabase
        .from('ledger_events')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: true });

      if (eventsError) throw eventsError;

      // Fetch funding rounds
      const { data: rounds, error: roundsError } = await supabase
        .from('funding_rounds')
        .select('*')
        .eq('company_id', companyId)
        .order('closed_at', { ascending: true });

      if (roundsError) throw roundsError;

      // Fetch subscription agreements
      const { data: subscriptions, error: subsError } = await supabase
        .from('subscription_agreements')
        .select('*')
        .eq('company_id', companyId)
        .eq('status', 'ISSUED')
        .order('issued_date', { ascending: true });

      if (subsError) throw subsError;

      // Build timeline by processing events chronologically
      const timelineData: TimelineEvent[] = [];
      let myRunningShares = 0;
      let totalRunningShares = 0;
      let previousOwnership = 0;

      // Process each ledger event
      events?.forEach((event) => {
        const eventDate = new Date(event.created_at);
        const shares = Number(event.shares) || 0;

        // Update running totals
        if (event.event_type === 'ISSUANCE' || event.event_type === 'SUBSCRIPTION') {
          if (event.shareholder_id === shareholderId) {
            myRunningShares += shares;
          }
          totalRunningShares += shares;
        } else if (event.event_type === 'TRANSFER') {
          // Handle transfers
          if (event.shareholder_id === shareholderId) {
            myRunningShares -= shares;
          }
          // Transfers don't change total shares
        } else if (event.event_type === 'CANCELLATION') {
          if (event.shareholder_id === shareholderId) {
            myRunningShares -= shares;
          }
          totalRunningShares -= shares;
        }

        const currentOwnership =
          totalRunningShares > 0 ? (myRunningShares / totalRunningShares) * 100 : 0;
        const changePct = currentOwnership - previousOwnership;

        // Find matching funding round
        const matchingRound = rounds?.find(
          (r) =>
            r.closed_at &&
            Math.abs(new Date(r.closed_at).getTime() - eventDate.getTime()) < 86400000 // Within 1 day
        );

        // Find matching subscription
        const matchingSubscription = subscriptions?.find(
          (s) => s.issued_date && s.ledger_event_id === event.id
        );

        let eventType: TimelineEvent['eventType'] = event.event_type as any;
        let description = `${event.event_type} - ${shares.toLocaleString()} shares`;

        if (matchingRound) {
          eventType = 'FUNDING_ROUND';
          description = `${matchingRound.instrument || 'Funding Round'} - ${shares.toLocaleString()} shares issued`;
        } else if (matchingSubscription) {
          eventType = 'SUBSCRIPTION';
          description = `Subscription Agreement - ${shares.toLocaleString()} shares`;
        }

        timelineData.push({
          date: format(eventDate, 'MMM dd, yyyy'),
          timestamp: eventDate.getTime(),
          eventType,
          description,
          myShares: myRunningShares,
          totalShares: totalRunningShares,
          ownershipPct: currentOwnership,
          changePct,
          sharePrice: event.price_per_share ? Number(event.price_per_share) : undefined,
          amount: event.price_per_share ? shares * Number(event.price_per_share) : undefined,
        });

        previousOwnership = currentOwnership;
      });

      setTimeline(timelineData);
    } catch (error) {
      console.error('Error fetching ownership history:', error);
      toast.error('Failed to load ownership history');
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    if (!timeline.length) return null;

    const first = timeline[0];
    const last = timeline[timeline.length - 1];
    const peak = timeline.reduce(
      (max, event) => (event.ownershipPct > max.ownershipPct ? event : max),
      timeline[0]
    );

    const dilutionEvents = timeline.filter((e) => e.changePct < -0.1).length;
    const totalDilution = last.ownershipPct - peak.ownershipPct;

    return {
      initialOwnership: first.ownershipPct,
      currentOwnership: last.ownershipPct,
      peakOwnership: peak.ownershipPct,
      peakDate: peak.date,
      totalChange: last.ownershipPct - first.ownershipPct,
      dilutionEvents,
      totalDilution,
      currentShares: last.myShares,
    };
  }, [timeline]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading ownership history...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (!timeline.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ownership History</CardTitle>
          <CardDescription>No ownership data available yet</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Initial Ownership</div>
            <div className="text-2xl font-bold">{stats?.initialOwnership.toFixed(2)}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Current Ownership</div>
            <div className="text-2xl font-bold text-primary">
              {stats?.currentOwnership.toFixed(2)}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Peak Ownership</div>
            <div className="text-2xl font-bold">{stats?.peakOwnership.toFixed(2)}%</div>
            <div className="text-xs text-muted-foreground mt-1">{stats?.peakDate}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Change</div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">
                {stats?.totalChange && stats.totalChange > 0 ? '+' : ''}
                {stats?.totalChange.toFixed(2)}%
              </span>
              {stats?.totalChange && stats.totalChange > 0 ? (
                <TrendingUp className="h-5 w-5 text-green-500" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-500" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Chart and Table */}
      <Card>
        <CardHeader>
          <CardTitle>Ownership Timeline</CardTitle>
          <CardDescription>
            Track your ownership percentage across {timeline.length} events •{stats?.dilutionEvents}{' '}
            dilution events detected
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="chart" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="chart">Visual Timeline</TabsTrigger>
              <TabsTrigger value="table">Detailed Events</TabsTrigger>
            </TabsList>

            <TabsContent value="chart" className="space-y-4">
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    label={{ value: 'Ownership %', angle: -90, position: 'insideLeft' }}
                    domain={[0, 'auto']}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload as TimelineEvent;
                        return (
                          <div className="bg-background border rounded-lg p-3 shadow-lg">
                            <p className="font-semibold">{data.date}</p>
                            <p className="text-sm text-muted-foreground">{data.description}</p>
                            <div className="mt-2 space-y-1">
                              <p className="text-sm">
                                <span className="font-medium">Ownership:</span>{' '}
                                {data.ownershipPct.toFixed(2)}%
                              </p>
                              <p className="text-sm">
                                <span className="font-medium">Change:</span>{' '}
                                <span
                                  className={data.changePct > 0 ? 'text-green-500' : 'text-red-500'}
                                >
                                  {data.changePct > 0 ? '+' : ''}
                                  {data.changePct.toFixed(2)}%
                                </span>
                              </p>
                              <p className="text-sm">
                                <span className="font-medium">Your Shares:</span>{' '}
                                {data.myShares.toLocaleString()}
                              </p>
                              <p className="text-sm">
                                <span className="font-medium">Total Shares:</span>{' '}
                                {data.totalShares.toLocaleString()}
                              </p>
                              {data.amount && (
                                <p className="text-sm">
                                  <span className="font-medium">Value:</span> $
                                  {data.amount.toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="ownershipPct"
                    fill="hsl(var(--primary) / 0.1)"
                    stroke="none"
                  />
                  <Line
                    type="monotone"
                    dataKey="ownershipPct"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  {stats?.peakOwnership && (
                    <ReferenceLine
                      y={stats.peakOwnership}
                      stroke="hsl(var(--chart-3))"
                      strokeDasharray="3 3"
                      label={{ value: 'Peak', position: 'right' }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>

              {/* Event Legend */}
              <div className="flex flex-wrap gap-4 justify-center pt-4">
                <div className="flex items-center gap-2">
                  <Circle className="h-3 w-3 fill-blue-500 text-blue-500" />
                  <span className="text-sm">Issuance</span>
                </div>
                <div className="flex items-center gap-2">
                  <Circle className="h-3 w-3 fill-purple-500 text-purple-500" />
                  <span className="text-sm">Subscription</span>
                </div>
                <div className="flex items-center gap-2">
                  <Circle className="h-3 w-3 fill-green-500 text-green-500" />
                  <span className="text-sm">Funding Round</span>
                </div>
                <div className="flex items-center gap-2">
                  <Circle className="h-3 w-3 fill-red-500 text-red-500" />
                  <span className="text-sm">Dilution Event</span>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="table">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead className="text-right">Your Shares</TableHead>
                      <TableHead className="text-right">Total Shares</TableHead>
                      <TableHead className="text-right">Ownership</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {timeline.map((event, index) => (
                      <TableRow key={index}>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            {event.date}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {event.eventType === 'FUNDING_ROUND' ? (
                              <Users className="h-4 w-4 text-green-500" />
                            ) : event.eventType === 'SUBSCRIPTION' ? (
                              <DollarSign className="h-4 w-4 text-purple-500" />
                            ) : (
                              <Circle className="h-3 w-3 text-blue-500" />
                            )}
                            <span className="text-sm">{event.description}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {event.myShares.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {event.totalShares.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {event.ownershipPct.toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge
                            variant={
                              event.changePct > 0
                                ? 'default'
                                : event.changePct < -0.1
                                  ? 'destructive'
                                  : 'secondary'
                            }
                          >
                            {event.changePct > 0 ? '+' : ''}
                            {event.changePct.toFixed(2)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {event.amount ? `$${event.amount.toLocaleString()}` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
